import ol = require("ol");
import _ = require("underscore");
import Portal = require("portal-gis");
import Utils = require("../utils");
import geo = require("../utils/geo");
import Mediator = require("../mediator");
import Services = require("../services/services");
import IPS = require("publicsector");
import Base = require("./base/geoquery");
import KeywordValidator = require("./base/keywords");

interface SelectorOptions {
    box?: Array<number>;
    point?: Array<number>;
}

interface LocatorOptions {
    location: string;
}

function doif<T>(v: T, cb: (v: T) => void) {
    if (typeof v !== "undefined") cb(v);
}

class Command extends Base<{ out: LocatorOptions | SelectorOptions }> {

    static about = `
Extends base/geoquery to execute a text or spatial search depending on input arguments.

Example Usage:

    <EVENT about="Search addresses for universal search value" event="universal-search" id="ips-address-locator" mid="app/commands/ips-geoquery-locator" text="Address Locator" type="find">
    <OPTIONS>
        <Values id="query-service" value="property/addresses" />
        <Values id="service-query-template" value="{&#xD;&#xA;    &quot;formulaName&quot;: &quot;FalseMapValidation&quot;,&#xD;&#xA;    &quot;Data&quot;: {&#xD;&#xA;        &quot;attributes&quot;: {&#xD;&#xA;            &quot;PARCLKEY&quot;: 10153454,&#xD;&#xA;            &quot;PARCLID&quot;: &quot;00005-44454-4546&quot;&#xD;&#xA;        }&#xD;&#xA;    }&#xD;&#xA;}" />
        <Values id="symbology" value="addresses" />
        <Values about="Once the results are gathered forward them to the 'geoquery-result' handler" id="event" value="geoquery-result" />
    </OPTIONS>
    </EVENT>

`;

    static options = Utils.defaults({
        "box": {
            "about": "an extent, transformed into x1 x2 y1 y2 to be template-friendly"
        },
        "event": {
            "default": "geoquery-result",
        },
        "key-template": {
            "default": "<%=id%>",
        },
        "location": {
            "about": "a description of a location"
        },
        "locatingproperties": {
            "about": "the attributes to examine to determine the feature location",
        },
        "point": {
            "about": "a selection point"
        },
        "query-service": {
            "about": "Public Sector REST endpoint"
        },
        "service-query-template": {
            "about": "filter template"
        },
    }, Base.options);

    static output = {
        "layers": {
            "about": "the search results",
        },
        "serviceInfo": {
            "about": "the input options",
        },
    };


    execute(args = this.args) {
        if (!KeywordValidator.validate(this)) return;

        doif(this.findOption("location") || undefined, v => this.executeLocator({
            location: v
        }));

        doif(this.findOption("box") || undefined, v => this.executeSelector({
            box: v
        }));
    }

    private runQuery(data: any) {

        let serviceInfo = this.options;
        Utils.ifOption(serviceInfo, "query-service", queryService => {

            let filter = "";
            const skipService = queryService && queryService.toLowerCase() === "crm/servicerequests" && typeof data.location == 'string';
            if(!skipService){
    
            Utils.ifOption(serviceInfo, "service-query-template", queryTemplate => {
                filter = _.template(queryTemplate)(data);
            });
        
            let service = new Services.BusinessService<IPS.GeoEntity>({
                query: {
                    query: filter,
                    merge: true
                }
            }, queryService);

            service.each().done(results => {

                let layer = <Rhythm.GIS.FeatureQueryResultLayer>{};

                layer.features = results.map(item => {
                    geo.beforeProcess({ data: item }, serviceInfo["locatingproperties"]);
                    if (geo.isPoint(item)) {
                        return {
                            geometryType: "point",
                            attributes: <any>item,
                            geometry: [item.xCoordinate, item.yCoordinate]
                        }
                    }
                    return {
                        attributes: <any>item
                    };
                });

                layer.spatialReference = { wkid: "EPSG:4326" };

                this.assignKeys(layer);

                this.trigger({
                    serviceInfo: serviceInfo,
                    layers: [layer]
                });

            });
        }
        });
    }

    private executeSelector(args: SelectorOptions) {
        let box = args.box;

        // make data template-friendly
        let data = {
            x1: box[0], y1: box[1],
            x2: box[2], y2: box[3]
        };

        // transform to ips coordinate system
        Utils.ifOption(this.options, "srs", srs => {
            let c = new ol.geom.MultiPoint([[data.x1, data.y1], [data.x2, data.y2]]);
            c.transform("EPSG:4326", srs);
            [data.x1, data.y1] = c.getFirstCoordinate();
            [data.x2, data.y2] = c.getLastCoordinate();
        });

        this.runQuery(data);
    }

    private executeLocator(args: LocatorOptions) {
        this.runQuery(args);
    }

}

export = Command;